/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH under
 * one or more contributor license agreements. See the NOTICE file distributed
 * with this work for additional information regarding copyright ownership.
 * Licensed under the Camunda License 1.0. You may not use this file
 * except in compliance with the Camunda License 1.0.
 */

/**
 * @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').TestDataSummary} TestDataSummary
 * @typedef {import('App/Pages/Project/IdpApplication/idp-project/idp-extraction-project/structured-extraction/extracted-results/types').TestSummaryDetailedResults} TestSummaryDetailedResults
 */

import { Table, TableHead, TableRow, TableHeader, TableBody, TableCell, ProgressBar, Button } from '@carbon/react';
import { View, TrashCan } from '@carbon/icons-react';

import { StyledTableRow } from './TestTemplateOverviewResults.styled';
import { ExtractionStatusIndicator } from './ExtractionStatusIndicator';

/**
 * @param {object} props
 * @param {TestSummaryDetailedResults[]} props.testSummaryDetailedResults
 * @param {Array<{id: string, name: string}>} props.documents
 * @param {TestDataSummary['testSummaryResults']} props.testSummaryResults
 * @param {(detail: TestSummaryDetailedResults, documentIndex: number) => void} props.onViewExtraction
 * @param {(documentId: string) => void} props.onDeleteDocument
 * @returns {JSX.Element}
 */
export const DetailedResultsTable = ({
  testSummaryDetailedResults,
  documents,
  testSummaryResults,
  onViewExtraction,
  onDeleteDocument
}) => {
  const getDetailedAverageNumberOfFieldsExtracted = (detail) => {
    return `${detail.fieldsExtracted} / ${testSummaryResults?.totalNumberOfFieldsExtracted ?? 0}`;
  };

  const getDetailedAverageNumberOfTablesExtracted = (detail) => {
    return `${detail.tablesExtracted} / ${testSummaryResults?.totalNumberOfTablesExtracted ?? 0}`;
  };

  /**
   * @param {TestSummaryDetailedResults} detail
   * @returns {JSX.Element|null}
   */

  return (
    <Table>
      <TableHead>
        <TableRow>
          <TableHeader>Filename</TableHeader>
          <TableHeader>Average Confidence</TableHeader>
          <TableHeader>Fields Extracted</TableHeader>
          <TableHeader>Tables Extracted</TableHeader>
          <TableHeader>Extraction Status</TableHeader>
          <TableHeader aria-label="View extraction" />
          <TableHeader aria-label="Remove" />
        </TableRow>
      </TableHead>
      <TableBody>
        {testSummaryDetailedResults.map((detail, idx) => {
          const document = documents[idx];
          if (!document) {
            return null;
          }

          return (
            <StyledTableRow key={document.id}>
              <TableCell>{document.name}</TableCell>
              <TableCell>
                <ProgressBar
                  value={detail.averageConfidence}
                  label={`${Math.round(detail.averageConfidence)}%`}
                  type="inline"
                  size="small"
                />
              </TableCell>
              <TableCell>{getDetailedAverageNumberOfFieldsExtracted(detail)}</TableCell>
              <TableCell>{getDetailedAverageNumberOfTablesExtracted(detail)}</TableCell>
              <TableCell>
                <ExtractionStatusIndicator status={detail.extractionStatus} />
              </TableCell>
              <TableCell>
                <Button
                  kind="ghost"
                  renderIcon={View}
                  iconDescription="View extraction"
                  size="sm"
                  onClick={() => onViewExtraction(detail, idx)}
                >
                  View Extraction
                </Button>
              </TableCell>
              <TableCell>
                <Button
                  kind="danger--ghost"
                  renderIcon={TrashCan}
                  iconDescription="Remove"
                  size="sm"
                  onClick={() => onDeleteDocument(document.id)}
                >
                  Remove
                </Button>
              </TableCell>
            </StyledTableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
